import { Box, Button, makeStyles, Modal, Tab, Tabs, Typography } from '@material-ui/core'
import { useWallet as useTronWallet } from '@tronweb3/tronwallet-adapter-react-hooks'
import { FC, ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { useConnect } from 'wagmi'
import coinbaseIcon from '../../assets/images/icons/coinbase.svg'
import metamaskIcon from '../../assets/images/icons/metamask.svg'
import ethereumIcon from '../../assets/images/img/ethereum.png'
import tronIcon from '../../assets/images/img/tron.png'
import walletConnectIcon from '../../assets/images/icons/walletconnect.svg'
import { LimitedAccessRight } from '../../graphql'
import { useDetermineUserRights, useIsLimitedContract } from '../../hooks'
import { useWeb3 } from '../../resources/web3'
import { PATH_PARAMS } from '../../routes/paths'
import { BlockchainType } from '../../resources/web3/adapters/types'
import config from '../../config'

const useStyles = makeStyles((theme) => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(4),
    borderRadius: theme.shape.borderRadius,
    maxWidth: 400,
    width: '100%',
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  connectorButton: {
    marginBottom: theme.spacing(1),
    width: '100%',
    justifyContent: 'flex-start',
    textTransform: 'none',
    padding: theme.spacing(1.5, 2),
  },
  connectorIcon: {
    width: 24,
    height: 24,
    marginRight: theme.spacing(1),
  },
  tabsContainer: {
    marginBottom: theme.spacing(2),
  },
  tabPanel: {
    marginTop: theme.spacing(2),
  },
}))

// Wallet icons lookup hash
const CONNECTOR_ICONS: Record<string, string> = {
  metamask: metamaskIcon,
  walletconnect: walletConnectIcon,
  coinbase: coinbaseIcon,
}

// Get connector icon from hash
const getConnectorIcon = (connectorId: string) => {
  const id = connectorId.toLowerCase()
  return CONNECTOR_ICONS[id] || CONNECTOR_ICONS.default
}

type ConnectCryptoWalletButtonParams = {
  connect: () => void
}

interface TabPanelProps {
  children?: ReactNode
  index: number
  value: number
  className?: string
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, className, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`wallet-tabpanel-${index}`}
      aria-labelledby={`wallet-tab-${index}`}
      className={className}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  )
}

function ConnectorTab(props: { icon: string; className: string; label: string }) {
  return (
    <Box display="flex" alignItems="center">
      <img src={props.icon} className={props.className} alt={props.label} />
      {props.label}
    </Box>
  )
}

export const ConnectCryptoWalletButton: FC<{
  children: (params: ConnectCryptoWalletButtonParams) => ReactNode
}> = ({ children }) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const [open, setOpen] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const { [PATH_PARAMS.applicationId]: applicationId } = useParams() as Record<string, string>
  const { userRights } = useDetermineUserRights(applicationId as string | number)
  const { cryptoPaymentsEnabled } = useIsLimitedContract()

  // TODO: abstract this into useBlockchainAdapter hook
  const { wallets: tronAdapters } = useTronWallet()
  const { connectors: ethereumAdapters } = useConnect()
  const { cryptoWallet, isPendingCheck, setApplicationId } = useWeb3()
  const { connect } = useWeb3()

  const isViewOnly = userRights?.limitedAccessRight === LimitedAccessRight.ViewOnly

  const handleOpen = () => setOpen(true)
  const handleClose = () => {
    setOpen(false)
    setActiveTab(0) // Reset to first tab when closing
  }

  const handleTabChange = (event: React.ChangeEvent<unknown>, newValue: number) => {
    setActiveTab(newValue)
  }

  const onConnect = () => {
    handleOpen()
  }

  const handleConnect = (blockchain: BlockchainType, connector: string) => {
    connect(blockchain, connector)
    handleClose()
  }

  useEffect(() => {
    setApplicationId && setApplicationId(applicationId)
  }, [applicationId, setApplicationId])

  return (
    <>
      {(config.crypto.enabled || config.featureTestContracts?.includes(applicationId)) &&
        !isViewOnly &&
        cryptoPaymentsEnabled &&
        !cryptoWallet &&
        !isPendingCheck &&
        children({
          connect: onConnect,
        })}

      <Modal
        className={classes.modal}
        open={open}
        onClose={handleClose}
        aria-labelledby="connect-wallet-modal"
        aria-describedby="connect-crypto-wallet-modal"
      >
        <Box className={classes.paper}>
          <Typography variant="h5" className={classes.title} id="connect-wallet-modal">
            {t('connectCryptoWallet.title', 'Connect Wallet')}
          </Typography>

          <Box className={classes.tabsContainer}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              aria-label="wallet connection tabs"
            >
              <Tab
                label={
                  <ConnectorTab
                    icon={ethereumIcon}
                    className={classes.connectorIcon}
                    label="Ethereum"
                  />
                }
                id="wallet-tab-0"
                aria-controls="wallet-tabpanel-0"
              />
              {(config.crypto.tronEnalbed ||
                config.featureTestContracts?.includes(applicationId)) && (
                <Tab
                  label={
                    <ConnectorTab icon={tronIcon} className={classes.connectorIcon} label="Tron" />
                  }
                  id="wallet-tab-1"
                  aria-controls="wallet-tabpanel-1"
                />
              )}
            </Tabs>
          </Box>

          <TabPanel value={activeTab} index={0} className={classes.tabPanel}>
            {ethereumAdapters.map((connector) => (
              <Button
                key={connector.id}
                variant="outlined"
                color="primary"
                className={classes.connectorButton}
                // disabled={!connector.ready}
                onClick={() => handleConnect(BlockchainType.ETHEREUM, connector.id)}
                startIcon={
                  <img
                    src={getConnectorIcon(
                      connector.id === 'injected' ? connector.name : connector.id,
                    )}
                    className={classes.connectorIcon}
                    alt={`${connector.name} icon`}
                  />
                }
              >
                {connector.name}
                {!connector.ready && ' (unsupported)'}
                {/* TODO: */}
                {/* {isLoading && connector.id === pendingConnector?.id && ' (connecting)'} */}
              </Button>
            ))}
          </TabPanel>

          <TabPanel value={activeTab} index={1} className={classes.tabPanel}>
            {tronAdapters.map((w) => (
              <Button
                key={w.adapter.name}
                variant="outlined"
                color="primary"
                className={classes.connectorButton}
                startIcon={
                  <img
                    src={w.adapter.icon}
                    className={classes.connectorIcon}
                    alt={`${w.adapter.name} icon`}
                  />
                }
                onClick={() => handleConnect(BlockchainType.TRON, w.adapter.name)}
              >
                {w.adapter.name}
              </Button>
            ))}
          </TabPanel>
        </Box>
      </Modal>
    </>
  )
}
